import { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, Settings, SkipForward, ArrowLeft, Subtitles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Hls from "hls.js";

export default function VideoPlayer({ src, title = "", onProgress, onEnd, onSkipNext, hasNext = false }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const hideTimeout = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    let hls;
    if (/\.m3u8($|\?)/i.test(src) && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true });
      hls.loadSource(src);
      hls.attachMedia(v);
    } else {
      v.src = src;
    }
    return () => { if (hls) hls.destroy(); };
  }, [src]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onLoaded = () => setDuration(v.duration);
    const onTime = () => {
      setProgress(v.currentTime);
      if (onProgress && v.duration) onProgress((v.currentTime / v.duration) * 100);
    };
    const onEnded = () => onEnd && onEnd();
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("ended", onEnded);
    };
  }, [onProgress, onEnd]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (videoRef.current) {
      videoRef.current.volume = val;
      videoRef.current.muted = val === 0;
      setMuted(val === 0);
    }
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    v.currentTime = pos * duration;
  };

  const fullscreen = () => {
    const c = containerRef.current;
    if (!document.fullscreenElement) c?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const skipForward = () => {
    if (videoRef.current) videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
  };

  const setPlaybackSpeed = (s) => {
    if (videoRef.current) videoRef.current.playbackRate = s;
    setSpeed(s);
    setShowSettings(false);
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => playing && setShowControls(false), 3000);
  };

  const fmt = (t) => {
    if (!t || isNaN(t)) return "00:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-screen bg-black select-none"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => playing && setShowControls(false)}
      data-testid="video-player"
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain bg-black"
        autoPlay
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onClick={togglePlay}
      />

      {/* Top bar */}
      <div className={`absolute top-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-b from-black/80 to-transparent transition-opacity ${showControls ? "opacity-100" : "opacity-0"}`}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-white hover:text-[#E50914]" data-testid="player-back-btn" aria-label="Back">
            <ArrowLeft size={28} />
          </button>
          <h2 className="text-lg sm:text-xl font-semibold text-white" style={{ fontFamily: "Outfit" }}>{title}</h2>
        </div>
      </div>

      {/* Skip Intro */}
      {progress > 5 && progress < 25 && (
        <button
          onClick={() => { if (videoRef.current) videoRef.current.currentTime = 30; }}
          className="absolute bottom-32 right-6 px-5 py-2.5 bg-white/15 backdrop-blur-md text-white text-sm font-semibold rounded border border-white/30 hover:bg-white/25"
          data-testid="skip-intro-btn"
        >
          Skip Intro
        </button>
      )}

      {/* Bottom controls */}
      <div className={`absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-t from-black/95 to-transparent transition-opacity ${showControls ? "opacity-100" : "opacity-0"}`}>
        {/* Progress bar */}
        <div className="mb-3 cursor-pointer h-1.5 bg-white/20 rounded-full overflow-hidden hover:h-2 transition-all" onClick={seek} data-testid="progress-bar">
          <div className="h-full bg-[#E50914]" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
        </div>

        <div className="flex items-center gap-3 sm:gap-5 text-white">
          <button onClick={togglePlay} className="hover:text-[#E50914]" data-testid="play-pause-btn" aria-label="Play/Pause">
            {playing ? <Pause size={26} className="fill-white" /> : <Play size={26} className="fill-white" />}
          </button>
          <button onClick={skipForward} className="hover:text-[#E50914] hidden sm:block" data-testid="skip-forward-btn" aria-label="Skip forward 10s">
            <SkipForward size={22} />
          </button>
          <div className="flex items-center gap-2 group/vol">
            <button onClick={toggleMute} className="hover:text-[#E50914]" data-testid="mute-btn" aria-label="Mute">
              {muted || volume === 0 ? <VolumeX size={22} /> : <Volume2 size={22} />}
            </button>
            <input type="range" min="0" max="1" step="0.05" value={muted ? 0 : volume} onChange={handleVolume} className="w-20 accent-[#E50914] hidden sm:block" />
          </div>
          <span className="text-xs sm:text-sm tabular-nums">{fmt(progress)} / {fmt(duration)}</span>
          <div className="flex-1" />
          <button className="hover:text-[#E50914] hidden md:block" data-testid="subtitles-btn" aria-label="Subtitles">
            <Subtitles size={22} />
          </button>
          <div className="relative">
            <button onClick={() => setShowSettings((v) => !v)} className="hover:text-[#E50914]" data-testid="settings-btn" aria-label="Settings">
              <Settings size={22} />
            </button>
            {showSettings && (
              <div className="absolute bottom-full mb-2 right-0 bg-black/95 border border-white/10 rounded-lg p-3 w-44">
                <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Speed</div>
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((s) => (
                  <button key={s} onClick={() => setPlaybackSpeed(s)} className={`block w-full text-left px-2 py-1 text-sm rounded ${speed === s ? "bg-[#E50914] text-white" : "text-white hover:bg-white/10"}`}>
                    {s === 1 ? "Normal" : `${s}x`}
                  </button>
                ))}
                <div className="text-xs uppercase tracking-wider text-white/50 mt-3 mb-2">Quality</div>
                {["Auto", "1080p", "720p", "480p"].map((q) => (
                  <button key={q} className="block w-full text-left px-2 py-1 text-sm text-white hover:bg-white/10 rounded">{q}</button>
                ))}
              </div>
            )}
          </div>
          {hasNext && (
            <button onClick={onSkipNext} className="hidden sm:flex items-center gap-1 text-sm hover:text-[#E50914]" data-testid="next-episode-btn">
              Next Ep
            </button>
          )}
          <button onClick={fullscreen} className="hover:text-[#E50914]" data-testid="fullscreen-btn" aria-label="Fullscreen">
            <Maximize size={22} />
          </button>
        </div>
      </div>
    </div>
  );
}
